import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Edit as EditIcon,
    Public as PublicIcon,
    Business as BusinessIcon,
    Visibility as VisibilityIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, usePermissions, useGetIdentity } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import WikiComments from './WikiComments';
import { canManageWikiArticles } from './wikiPermissions';
import {
    Book as BookIcon,
    Help as HelpIcon,
    Settings as SettingsIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Favorite as FavoriteIcon,
    Home as HomeIcon,
    Work as WorkIcon,
    School as SchoolIcon,
    Computer as ComputerIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Security as SecurityIcon,
    Build as BuildIcon,
    Code as CodeIcon,
    CloudQueue as CloudIcon,
    Storage as StorageIcon,
    Assignment as AssignmentIcon,
    Description as DescriptionIcon,
    Folder as FolderIcon,
    Label as LabelIcon,
    AttachFile as AttachIcon,
    InsertDriveFile as FileIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon,
    PictureAsPdf as PdfIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';

interface WikiArticle {
    id: number;
    title: string;
    slug: string;
    status: 'draft' | 'published' | 'archived';
    summary?: string;
    companyId?: number;
    category?: {
        id: number;
        name: string;
        color: string;
        icon?: string;
    };
    currentVersion?: any;
    createdAt: string;
    updatedAt: string;
    publishedAt?: string;
    viewCount: number;
}

const iconMap: Record<string, React.ComponentType> = {
    'book': BookIcon,
    'help': HelpIcon,
    'settings': SettingsIcon,
    'info': InfoIcon,
    'warning': WarningIcon,
    'star': StarIcon,
    'favorite': FavoriteIcon,
    'home': HomeIcon,
    'work': WorkIcon,
    'school': SchoolIcon,
    'computer': ComputerIcon,
    'phone': PhoneIcon,
    'email': EmailIcon,
    'security': SecurityIcon,
    'build': BuildIcon,
    'code': CodeIcon,
    'cloud_queue': CloudIcon,
    'storage': StorageIcon,
    'assignment': AssignmentIcon,
    'description': DescriptionIcon,
    'folder': FolderIcon,
    'label': LabelIcon
};

const WikiViewer: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [article, setArticle] = useState<WikiArticle | null>(null);
    const [content, setContent] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [isFavorite, setIsFavorite] = useState(false);
    const [favoriteId, setFavoriteId] = useState<number | null>(null);
    const [attachments, setAttachments] = useState<any[]>([]);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const { data: identity } = useGetIdentity();

    // #304: kanonischer Wiki-Edit-Rollensatz inkl. ROLE_COMPANY_ADMIN —
    // vorher fehlte er, daher war der Bearbeiten-Button fuer Company-Admins weg.
    const canEdit = canManageWikiArticles(permissions);

    useEffect(() => {
        if (slug) {
            loadArticle(slug);
        }
    }, [slug]);

    useEffect(() => {
        if (article && identity) {
            checkFavoriteStatus();
        }
    }, [article, identity]);

    const loadArticle = async (articleSlug: string) => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('wiki_articles', {
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'ASC' },
                filter: { slug: articleSlug }
            });

            if (data.length === 0) {
                notify('Artikel nicht gefunden', { type: 'error' });
                navigate('/wiki');
                return;
            }

            const loadedArticle = data[0];

            // Load category if it's just an ID
            if (loadedArticle.category && typeof loadedArticle.category === 'string') {
                try {
                    let categoryId: number;
                    const match = loadedArticle.category.match(/\/(\d+)$/);
                    if (match) {
                        categoryId = parseInt(match[1], 10);
                    } else {
                        categoryId = parseInt(loadedArticle.category, 10);
                    }

                    const { data: categoryData } = await dataProvider.getOne('wiki_categories', { id: categoryId });
                    loadedArticle.category = categoryData;
                } catch (err) {
                    console.error('Failed to load category:', err);
                }
            }

            setArticle(loadedArticle);

            // Load attachments via dedicated endpoint
            try {
                const token = localStorage.getItem('token');
                const attRes = await fetch(`${process.env.REACT_APP_API_URL}/api/wiki-articles/${loadedArticle.id}/attachments`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (attRes.ok) {
                    const attData = await attRes.json();
                    setAttachments(attData.attachments || []);
                }
            } catch (err) {
                console.error('[WikiViewer] Failed to load attachments:', err);
            }

            // Load current version content
            if (loadedArticle.currentVersion) {
                let versionId: number;

                if (typeof loadedArticle.currentVersion === 'object') {
                    versionId = loadedArticle.currentVersion.id;
                } else if (typeof loadedArticle.currentVersion === 'string') {
                    const match = loadedArticle.currentVersion.match(/\/(\d+)$/);
                    versionId = match ? parseInt(match[1], 10) : parseInt(loadedArticle.currentVersion, 10);
                } else {
                    versionId = parseInt(loadedArticle.currentVersion, 10);
                }

                const { data: versionData } = await dataProvider.getOne('wiki_versions', { id: versionId });
                setContent(versionData.content || '');
            } else {
                // Try to load latest version
                const { data: versions } = await dataProvider.getList('wiki_versions', {
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: 'versionNumber', order: 'DESC' },
                    filter: { article: `/api/wiki_articles/${loadedArticle.id}` }
                });

                if (versions.length > 0) {
                    setContent(versions[0].content || '');
                }
            }

            // Increment view count
            try {
                await dataProvider.update('wiki_articles', {
                    id: loadedArticle.id,
                    data: { viewCount: loadedArticle.viewCount + 1 },
                    previousData: loadedArticle
                });
            } catch (err) {
                // Ignore error if view count update fails
                console.error('Failed to update view count:', err);
            }
        } catch (error) {
            notify('Fehler beim Laden des Artikels', { type: 'error' });
            navigate('/wiki');
        } finally {
            setLoading(false);
        }
    };

    const checkFavoriteStatus = async () => {
        if (!article || !identity?.id) return;

        try {
            const { data } = await dataProvider.getList('wiki_favorites', {
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'ASC' },
                filter: { userId: identity.id, 'article.id': article.id }
            });

            if (data.length > 0) {
                setIsFavorite(true);
                setFavoriteId(data[0].id);
            } else {
                setIsFavorite(false);
                setFavoriteId(null);
            }
        } catch (error) {
            console.error('Error checking favorite status:', error);
        }
    };

    const toggleFavorite = async () => {
        if (!article || !identity?.id) return;

        try {
            if (isFavorite && favoriteId) {
                // Remove favorite
                await dataProvider.delete('wiki_favorites', {
                    id: favoriteId,
                    previousData: {} as any
                });
                setIsFavorite(false);
                setFavoriteId(null);
                notify('Artikel aus Favoriten entfernt', { type: 'success' });
            } else {
                // Add favorite
                const { data } = await dataProvider.create('wiki_favorites', {
                    data: {
                        article: `/api/wiki_articles/${article.id}`,
                        userId: identity.id
                    }
                });
                setIsFavorite(true);
                setFavoriteId(data.id);
                notify('Artikel zu Favoriten hinzugefügt', { type: 'success' });
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
            notify('Fehler beim Aktualisieren der Favoriten', { type: 'error' });
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!article) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Artikel nicht gefunden</Typography>
            </Box>
        );
    }

    const categoryIcon = article.category?.icon ? iconMap[article.category.icon] : null;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/wiki')}>
                    Zurück
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        onClick={handlePrint}
                        title="Artikel drucken"
                        className="no-print"
                    >
                        <PrintIcon />
                    </IconButton>
                    <IconButton
                        color={isFavorite ? 'warning' : 'default'}
                        onClick={toggleFavorite}
                        title={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                        className="no-print"
                    >
                        {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                    {canEdit && (
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/wiki/${article.slug}/edit`)}
                            className="no-print"
                        >
                            Bearbeiten
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 4 }}>
                {/* Title */}
                <Typography variant="h3" component="h1" gutterBottom>
                    {article.title}
                </Typography>

                {/* Meta Information */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {article.category && (
                        <Chip
                            label={article.category.name}
                            size="small"
                            icon={categoryIcon ? React.createElement(categoryIcon) : undefined}
                            sx={{
                                backgroundColor: article.category.color,
                                color: '#fff',
                                '& .MuiChip-icon': { color: '#fff !important' }
                            }}
                        />
                    )}
                    {article.companyId ? (
                        <Chip icon={<BusinessIcon />} label="Firma" size="small" />
                    ) : (
                        <Chip icon={<PublicIcon />} label="FAQ" size="small" />
                    )}
                    <Chip
                        icon={<VisibilityIcon />}
                        label={`${article.viewCount} Aufrufe`}
                        size="small"
                        variant="outlined"
                    />
                </Box>

                {/* Summary */}
                {article.summary && (
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 3, fontStyle: 'italic' }}
                    >
                        {article.summary}
                    </Typography>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Content */}
                <Box
                    sx={{
                        '& h1': { fontSize: '2em', fontWeight: 'bold', marginTop: '0.67em', marginBottom: '0.67em' },
                        '& h2': { fontSize: '1.5em', fontWeight: 'bold', marginTop: '0.75em', marginBottom: '0.75em' },
                        '& h3': { fontSize: '1.17em', fontWeight: 'bold', marginTop: '0.83em', marginBottom: '0.83em' },
                        '& p': { marginTop: '1em', marginBottom: '1em', lineHeight: 1.6 },
                        '& ul, & ol': { paddingLeft: '2em', marginTop: '1em', marginBottom: '1em' },
                        '& li': { marginBottom: '0.5em' },
                        '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' },
                        '& pre': { backgroundColor: '#f5f5f5', padding: '1em', borderRadius: '4px', overflow: 'auto', marginTop: '1em', marginBottom: '1em' },
                        '& a': { color: 'primary.main', textDecoration: 'underline' },
                        '& img': { maxWidth: '100%', height: 'auto' },
                        '& blockquote': { borderLeft: '4px solid', borderColor: 'divider', paddingLeft: '1em', marginLeft: 0, fontStyle: 'italic' }
                    }}
                    dangerouslySetInnerHTML={{ __html: content }}
                />

                <Divider sx={{ my: 3 }} />

                {/* Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        Erstellt: {formatDate(article.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Aktualisiert: {formatDate(article.updatedAt)}
                    </Typography>
                </Box>
            </Paper>

            {/* Attachments Section */}
            {attachments.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AttachIcon /> Anhänge ({attachments.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {attachments.map((file) => {
                            const getIcon = () => {
                                if (!file.mimeType) return <FileIcon color="action" />;
                                if (file.mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
                                if (file.mimeType.startsWith('video/')) return <VideoIcon color="secondary" />;
                                if (file.mimeType.startsWith('audio/')) return <AudioIcon color="info" />;
                                if (file.mimeType === 'application/pdf') return <PdfIcon color="error" />;
                                return <FileIcon color="action" />;
                            };
                            const size = typeof file.fileSize === 'string' ? parseInt(file.fileSize) : (file.fileSize || file.size || 0);
                            const sizeStr = size < 1024 ? `${size} B` : size < 1048576 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1048576).toFixed(1)} MB`;
                            return (
                                <Paper key={file.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {getIcon()}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" noWrap>{file.originalFilename}</Typography>
                                        <Typography variant="caption" color="text.secondary">{sizeStr}</Typography>
                                    </Box>
                                    {file.uuid && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<DownloadIcon />}
                                            onClick={async () => {
                                                try {
                                                    const token = localStorage.getItem('token');
                                                    const response = await fetch(
                                                        `${process.env.REACT_APP_API_URL}/api/media/download/${file.uuid}`,
                                                        { headers: { 'Authorization': `Bearer ${token}` } }
                                                    );
                                                    if (!response.ok) throw new Error('Download failed');
                                                    const blob = await response.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = file.originalFilename || 'download';
                                                    a.click();
                                                    window.URL.revokeObjectURL(url);
                                                } catch { notify('Fehler beim Herunterladen', { type: 'error' }); }
                                            }}
                                        >
                                            Herunterladen
                                        </Button>
                                    )}
                                </Paper>
                            );
                        })}
                    </Box>
                </Paper>
            )}

            {/* Comments Section */}
            <Box className="no-print">
                <WikiComments articleId={article.id} />
            </Box>
        </Box>
    );
};

export default WikiViewer;
